import { useEffect, useRef, useState, type ReactNode } from "react";
import { IconChevronLeft, IconClose, IconPencil } from "./icons";
import { PHONE_QUERY } from "../lib/useMediaQuery";

/**
 * Terugpijl linksboven in een detailpaneel (asset, bevinding, gebouw). Alleen op de telefoon zichtbaar,
 * waar het paneel het hele scherm vult; op desktop blijft het kruisje rechts (UX-RICHTLIJNEN §10).
 */
export function DetailBack({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" className="btn ghost ico dback" onClick={onClose} aria-label="Terug" title="Terug">
      <IconChevronLeft size={24} aria-hidden="true" />
    </button>
  );
}

/**
 * Kop van elk paneel (UX-RICHTLIJNEN §6, Paneel): één regel met terugpijl (telefoon), code of bovenschrift,
 * chip, acties, Bewerken (potlood) en het kruisje (desktop); daaronder de titel en een metaregel. Gebruikt door
 * asset, bevinding, onderhoud bij het gebouw, persoon, document, artikel en de lades in de planning.
 */
export function PanelHead({
  code,
  eyebrow,
  chip,
  title,
  titleId,
  sub,
  actions,
  onEdit,
  editLabel = "Bewerken",
  onClose,
  children,
}: {
  /** Code in paars, zoals `XX4-JNW` of `LWEI-0001`. */
  code?: ReactNode;
  /** Bovenschrift als er geen code is, zoals het asset bij een planningsregel. */
  eyebrow?: ReactNode;
  chip?: ReactNode;
  title?: ReactNode;
  titleId?: string;
  sub?: ReactNode;
  /** Extra knoppen rechts, zoals het ⋯-menu. */
  actions?: ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  onClose: () => void;
  /** Extra regel(s) onder de titel, zoals de statuskeuze van een bevinding. */
  children?: ReactNode;
}) {
  // Zonder code of bovenschrift staat de titel op de regel met de knoppen (geen lege regel boven de titel).
  const inline = code == null && eyebrow == null && chip == null;
  const heading =
    title != null ? (
      <h2 className={`dtitle${inline ? " grow" : ""}`} id={titleId}>
        {title}
      </h2>
    ) : null;
  return (
    <header className="dhead">
      <div className={`row${inline ? " dhead-inline" : ""}`}>
        <DetailBack onClose={onClose} />
        {code != null && <span className="code">{code}</span>}
        {eyebrow != null && <span className="deyebrow">{eyebrow}</span>}
        {chip}
        {inline ? (heading ?? <span className="grow" />) : <span className="grow" />}
        {actions}
        {onEdit && (
          <button
            type="button"
            className="btn ghost small ico"
            onClick={onEdit}
            aria-label={editLabel}
            title={editLabel}
          >
            <IconPencil size={18} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="btn ghost small ico dclose"
          onClick={onClose}
          aria-label="Sluiten"
          title="Sluiten"
        >
          <IconClose size={20} aria-hidden="true" />
        </button>
      </div>
      {!inline && heading}
      {sub != null && <div className="dsub">{sub}</div>}
      {children}
    </header>
  );
}

const CLOSE_AT = 96; // px omlaag (of snel vegen) en het paneel sluit
const FLICK = 0.6; // px per ms

/**
 * Omlaag vegen sluit een detailpaneel of venster (sheet) op de telefoon. Werkt vanaf de kop, of vanaf de inhoud als die
 * bovenaan staat. Het gebaar wordt hier afgehandeld (preventDefault), zodat de browser de pagina niet
 * herlaadt (pull-to-refresh). Zijwaarts vegen en scrollen in de inhoud blijven gewoon werken.
 */
export function useSwipeClose<T extends HTMLElement>(onClose: () => void) {
  // Callback-ref: het paneel wisselt van element (laden → inhoud), dan verhuizen de luisteraars mee.
  const [el, setEl] = useState<T | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!el) return;
    const phone = window.matchMedia(PHONE_QUERY);
    let startY = 0;
    let startX = 0;
    let startT = 0;
    let dy = 0;
    let mode: "idle" | "maybe" | "drag" | "off" = "idle";

    const scroller = (t: EventTarget | null) =>
      t instanceof Element ? t.closest<HTMLElement>(".dbody, .mbody, .picklist, .bswitch-list, .pl-dbody") : null;
    const reset = () => {
      el.style.transition = "";
      el.style.transform = "";
      el.removeAttribute("data-drag");
    };
    const onStart = (e: TouchEvent) => {
      if (!phone.matches || e.touches.length !== 1) return void (mode = "off");
      const t = e.target as Element;
      // Niet in velden, keuzelijsten of iets dat zelf zijwaarts schuift (tabs, tabellen).
      if (t.closest("input, textarea, select, [contenteditable], .tabs, .tscroll")) return void (mode = "off");
      // Een venster boven dit paneel handelt zijn eigen gebaren af.
      if (t.closest(".modalbg") !== el.closest(".modalbg")) return void (mode = "off");
      const body = scroller(t);
      if (body && body.scrollTop > 0) return void (mode = "off");
      const p = e.touches[0];
      if (!p) return void (mode = "off");
      startY = p.clientY;
      startX = p.clientX;
      startT = e.timeStamp;
      dy = 0;
      mode = "maybe";
    };
    const onMove = (e: TouchEvent) => {
      if (mode === "off" || mode === "idle") return;
      const p = e.touches[0];
      if (!p) return;
      const y = p.clientY - startY;
      const x = p.clientX - startX;
      if (mode === "maybe") {
        if (Math.abs(x) > Math.abs(y) || y < 0) return void (mode = "off");
        if (y < 6) return;
        mode = "drag";
        el.setAttribute("data-drag", "");
        el.style.transition = "none";
      }
      e.preventDefault();
      dy = Math.max(0, y);
      el.style.transform = `translateY(${dy}px)`;
    };
    const onEnd = (e: TouchEvent) => {
      if (mode !== "drag") return void (mode = "idle");
      mode = "idle";
      const fast = dy / Math.max(1, e.timeStamp - startT) > FLICK;
      el.style.transition = "transform var(--t-med) var(--ease-out)";
      if (dy > CLOSE_AT || (fast && dy > 24)) {
        el.style.transform = "translateY(100%)";
        const done = () => close.current();
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) done();
        else window.setTimeout(done, 180);
      } else {
        el.style.transform = "";
        window.setTimeout(reset, 220);
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [el]);
  return setEl;
}
